import * as Sentry from '@sentry/react';
import type { ConfigType } from '@provider-portal/common/config';
import { historyInstance } from '@provider-portal/common/utils/history';
import { getAppVersion } from '@provider-portal/common/utils/appVersion';
import { LanguageUtils } from '@provider-portal/common/utils/translations';
import { shouldReportErrorToSentry } from '@provider-portal/common/utils/errorReporting';
import { matchPath } from 'react-router';

export function initSentry(config: ConfigType): void {
  const environment = process.env.REACT_APP_STAGE;

  Sentry.init({
    dsn: config.sentry.dsn,
    environment,
    release: `provider-portal@${config.version}`,
    enabled: environment !== 'local',
    sampleRate: 0.1,
    tracesSampleRate: 0.0025,
    sendDefaultPii: false,
    ignoreErrors: [
      'Connection closed',
      'No refresh token available',
      'Invalid Permissions',
      'Error invoking postEvent',
    ],
    integrations: [
      Sentry.reactRouterV5BrowserTracingIntegration({
        history: historyInstance,
        matchPath,
      }),
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;
      if (error instanceof Error && !shouldReportErrorToSentry(error)) {
        return null;
      }

      const { appVersion, moduleName, moduleVersion } = getAppVersion();

      event.contexts = {
        ...event.contexts,
        module: {
          name: moduleName,
          version: moduleVersion,
        },
      };

      event.tags = {
        ...event.tags,
        module: moduleName,
        moduleVersion: moduleVersion,
        appVersion: appVersion,
      };

      event.dist = `${moduleName}-${moduleVersion}`;

      return event;
    },
    initialScope: {
      tags: {
        locale: LanguageUtils.getAppLocaleWithFallback(),
      },
    },
  });
}
